import { useCallback, useEffect, useRef } from "react";
import { IKeyboardNavigationApi } from "../../types/keyboard-navigation";

export function useKeyboardNavigation(
    getter: () => (HTMLElement | null)[]
): IKeyboardNavigationApi {
    const getterRef = useRef(getter);

    useEffect(() => {
        getterRef.current = getter;
    }, [getter]);

    const hasSelectedNavElement = useCallback(() => {
        const elements = getterRef.current().filter(Boolean) as HTMLElement[];
        return elements.findIndex((el) => el.classList.contains("is-selected")) !== -1;
    }, []);

    const applySelection = useCallback((index: number | null) => {
        const elements = getterRef.current().filter(Boolean) as HTMLElement[];
        elements.forEach((el) => el.classList.remove("is-selected"));
        if (index !== null && index >= 0 && index < elements.length) {
            elements[index].classList.add("is-selected");
        }
    }, []);

    const applyTouch = useCallback((index: number | null) => {
        const elements = getterRef.current().filter(Boolean) as HTMLElement[];
        elements.forEach((el) => el.classList.remove("is-touched"));
        if (index !== null && index >= 0 && index < elements.length) {
            elements[index].classList.add("is-touched");
        }
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const elements = getterRef.current().filter(Boolean) as HTMLElement[];
            if (elements.length === 0) return;

            let currentIndex = elements.findIndex((el) => el.classList.contains("is-selected"));

            const move = (direction: 1 | -1) => {
                let nextIndex: number;

                if (currentIndex === -1) {
                    nextIndex = direction === 1 ? -1 : elements.length;
                } else {
                    nextIndex = currentIndex;
                }

                for (let i = 0; i < elements.length; i++) {
                    nextIndex = (nextIndex + direction + elements.length) % elements.length;
                    const el = elements[nextIndex];
                    if (!el.classList.contains("not-selectable")) {
                        break;
                    }
                }
                applySelection(nextIndex);
                currentIndex = nextIndex;
            };

            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    move(1);
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    move(-1);
                    break;
                case "Enter":
                    event.preventDefault();
                    if (currentIndex >= 0) {
                        const el = elements[currentIndex];
                        if (el?.tagName.toLowerCase() === "a") {
                            el.click();
                        } else {
                            el?.getElementsByTagName("a")[0]?.click();
                        }
                    }
                    break;
            }
        },
        [applySelection]
    );

    const handleMouseSelect = useCallback(
        (index: number) => {
            applySelection(index);
        },
        [applySelection]
    );

    const handleMouseDeselect = useCallback(() => {
        applySelection(null);
    }, [applySelection]);

    const handleTouched = useCallback(
        (index: number) => {
            applyTouch(index);
        },
        [applyTouch]
    );

    const resetNavigation = useCallback(() => {
        getterRef.current = () => [];
    }, []);

    return {
        handleKeyDown,
        resetNavigation,
        hasSelectedNavElement,
        handleMouseSelect,
        handleMouseDeselect,
        handleTouched
    };
}
